import { z } from "zod";
import { createServerFn } from "@tanstack/react-start";
import { writeFileSync, readFileSync, existsSync, mkdirSync } from "node:fs";
import { join } from "node:path";
import type { AndroidProductsRawResponse, GooglePlayResponse } from "@/lib/types";
import { SUBSCRIPTION_SKUS } from "@/config/constants";

// Cache configuration
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds
const CACHE_FILE_PATH = join(process.cwd(), ".cache", "android-products-raw.json");

// In-memory cache
let memoryCache: {
  data: GooglePlayResponse | null;
  timestamp: number;
} | null = null;

const getAndroidProductsInputSchema = z.object({
  regionCode: z.string().optional().default("US"),
});

export const getAndroidProducts = createServerFn({ method: "GET" })
  .validator(getAndroidProductsInputSchema)
  .handler(async (ctx) => {
    try {
      const { regionCode } = ctx.data;

      // Check memory cache first
      if (memoryCache?.data && isValidCache(memoryCache.timestamp)) {
        return (
          extractRawSubscriptions(memoryCache.data, regionCode) ||
          getFallbackRawResponse(regionCode)
        );
      }

      // Check file cache
      const fileCachedData = loadFromFileCache();
      if (fileCachedData?.data) {
        memoryCache = {
          data: fileCachedData.data,
          timestamp: fileCachedData.timestamp,
        };
        return (
          extractRawSubscriptions(fileCachedData.data, regionCode) ||
          getFallbackRawResponse(regionCode)
        );
      }

      // Fetch from Google Play API
      const freshData = await fetchFromGooglePlayAPI();
      if (freshData) {
        const timestamp = Date.now();
        memoryCache = { data: freshData, timestamp };
        saveToFileCache(freshData, timestamp);
        return extractRawSubscriptions(freshData, regionCode) || getFallbackRawResponse(regionCode);
      }

      return getFallbackRawResponse(regionCode);
    } catch (error) {
      return getFallbackRawResponse("US");
    }
  });

// Helper functions
function isValidCache(timestamp: number): boolean {
  return Date.now() - timestamp < CACHE_DURATION;
}

function saveToFileCache(data: GooglePlayResponse, timestamp: number): void {
  try {
    const cacheDir = join(process.cwd(), ".cache");
    if (!existsSync(cacheDir)) {
      mkdirSync(cacheDir, { recursive: true });
    }

    const cacheData = {
      data,
      timestamp,
      expires: timestamp + CACHE_DURATION,
    };

    writeFileSync(CACHE_FILE_PATH, JSON.stringify(cacheData, null, 2), "utf-8");
  } catch (error) {
    // Silently fail cache save
  }
}

function loadFromFileCache(): { data: GooglePlayResponse; timestamp: number } | null {
  try {
    if (!existsSync(CACHE_FILE_PATH)) {
      return null;
    }

    const cacheContent = readFileSync(CACHE_FILE_PATH, "utf-8");
    const cacheData = JSON.parse(cacheContent);

    if (isValidCache(cacheData.timestamp)) {
      return {
        data: cacheData.data,
        timestamp: cacheData.timestamp,
      };
    }

    return null;
  } catch (error) {
    return null;
  }
}

async function fetchFromGooglePlayAPI(): Promise<GooglePlayResponse | null> {
  try {
    const packageName = process.env.GOOGLE_PLAY_PACKAGE_NAME || "ai.progressmade";
    const accessToken = await getAccessToken();

    if (!accessToken) {
      return null;
    }

    const response = await fetch(
      `https://androidpublisher.googleapis.com/androidpublisher/v3/applications/${packageName}/subscriptions`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      return null;
    }

    const responseText = await response.text();
    return JSON.parse(responseText) as GooglePlayResponse;
  } catch (error) {
    return null;
  }
}

export async function getAccessToken(): Promise<string | null> {
  try {
    const { GoogleAuth } = await import("google-auth-library");
    const path = await import("node:path");

    const serviceAccountPath = path.join(process.cwd(), "scripts", "serviceAccount.json");

    const auth = new GoogleAuth({
      keyFilename: serviceAccountPath,
      scopes: ["https://www.googleapis.com/auth/androidpublisher"],
    });

    const authClient = await auth.getClient();
    const accessToken = await authClient.getAccessToken();

    return accessToken.token || null;
  } catch (error) {
    return null;
  }
}

function extractRawSubscriptions(
  data: GooglePlayResponse,
  regionCode: string,
): AndroidProductsRawResponse | null {
  try {
    const subscriptions = data.subscriptions || [];

    const monthlyProduct = subscriptions.find((p) => p.productId === SUBSCRIPTION_SKUS.monthly);
    const yearlyProduct = subscriptions.find((p) => p.productId === SUBSCRIPTION_SKUS.yearly);

    // Get all available regions
    const availableRegions = new Set<string>();
    for (const sub of subscriptions) {
      for (const config of sub.basePlans?.[0]?.regionalConfigs || []) {
        availableRegions.add(config.regionCode);
      }
    }

    return {
      monthly: monthlyProduct || null,
      yearly: yearlyProduct || null,
      requestedRegion: regionCode,
      availableRegions: Array.from(availableRegions).sort(),
    };
  } catch (error) {
    return null;
  }
}

function getFallbackRawResponse(regionCode: string): AndroidProductsRawResponse {
  return {
    monthly: null,
    yearly: null,
    requestedRegion: regionCode,
    availableRegions: [],
  };
}
